////////////////////////////////////////////
// PER-ROUTE SEO — TITLE, DESCRIPTION, CANONICAL, OG/TWITTER, ROBOTS
////////////////////////////////////////////
//
// THE SHELL IS ONE STATIC index.html FOR EVERY ROUTE, SO ITS DEFAULTS ARE
// ONLY CORRECT FOR "/" — WITHOUT THIS, EVERY ROUTE WOULD SELF-CANONICALIZE
// TO THE HOMEPAGE, TELLING GOOGLE TO IGNORE THE REST OF THE SITE. THIS HOOK
// OVERWRITES THE STATIC DEFAULTS ONCE THE APP MOUNTS; KEEP index.html'S
// DEFAULTS IN SYNC WITH THE "/" ENTRY BELOW FOR NO-JS CRAWLERS.
import { useEffect } from "react";

const ORIGIN = "https://litfuze.fun";

interface RouteMeta {
  title: string;
  description: string;
}

const ROUTES: Record<string, RouteMeta> = {
  "/": {
    title: "Fuze — the anti-rug launchpad",
    description: "Sniper-proof launches on Solana and Robinhood Chain. Same entry price for every buyer, 99% back if the goal doesn't fill.",
  },
  "/discover": {
    title: "Discover launches — Fuze",
    description: "Browse live and past fair-launch tokens on Fuze — pump.fun and Robinhood Chain, verified on-chain.",
  },
  "/create": {
    title: "Launch a token — Fuze fair launch",
    description: "Set a goal and a countdown. The crowd fills it, one transaction launches it — no snipers, same price for everyone.",
  },
  "/create-instant": {
    title: "Instant launch — Fuze",
    description: "Fund the pool and it opens the moment you sign. One-signature token launch on Robinhood Chain.",
  },
  "/howitworks": {
    title: "How it works — Fuze",
    description: "How Fuze's fair-launch and instant-launch mechanics stop snipers and guarantee one entry price for everyone.",
  },
  "/legal": {
    title: "Legal — Fuze",
    description: "Fuze terms and disclosures.",
  },
  "/login": {
    title: "Sign in — Fuze",
    description: "Sign in to Fuze.",
  },
};

function setMeta(attr: "name" | "property", key: string, content: string) {
  let el = document.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setCanonical(href: string) {
  let el = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

// pathname: current route. launchKey: the launch address when present via the
// LEGACY ?launch= query form (null otherwise) — used only to redirect the
// canonical away from itself; the real per-launch copy lives at /launch/<addr>
// (docs/GROWTH.md unit 6).
export function useSeo(pathname: string, launchKey: string | null) {
  useEffect(() => {
    // /launch/<addr> — THE SERVER ALREADY INJECTED THE CORRECT PER-LAUNCH
    // title/description/OG BEFORE THIS EFFECT EVER RUNS (handleLaunchPage in
    // web/server/handlers.mjs) — don't clobber them. A client-side SPA nav
    // between launches won't refresh title/description (cosmetic only), but
    // canonical/og:url always self-correct so a mid-session share never
    // points at the wrong launch.
    if (pathname.startsWith("/launch/")) {
      setMeta("property", "og:url", ORIGIN + pathname);
      setCanonical(ORIGIN + pathname);
      setMeta("name", "robots", "index, follow");
      return;
    }

    // "/" + ?launch=<key> — the pre-unit-6 query-string form. Keeps working
    // (old links, in-app share buttons) but must NOT canonicalize to "/" (the
    // homepage) — point it at the indexable path instead.
    if (launchKey) {
      setCanonical(`${ORIGIN}/launch/${encodeURIComponent(launchKey)}`);
      setMeta("name", "robots", "index, follow");
      return;
    }

    const meta = ROUTES[pathname] || ROUTES["/"];
    const noindex = pathname === "/login" || pathname.startsWith("/admin");

    document.title = meta.title;
    setMeta("name", "description", meta.description);
    setMeta("name", "robots", noindex ? "noindex, nofollow" : "index, follow");
    setMeta("property", "og:title", meta.title);
    setMeta("property", "og:description", meta.description);
    setMeta("property", "og:url", ORIGIN + pathname);
    setMeta("name", "twitter:title", meta.title);
    setMeta("name", "twitter:description", meta.description);
    setCanonical(ORIGIN + pathname);
  }, [pathname, launchKey]);
}
